/**
 * Tyndo service-worker registration.
 *
 * Uses `workbox-window`'s {@link Workbox} helper so the shell can listen for
 * waiting/activated lifecycle events and auto-update on the next navigation.
 *
 * The SW itself lives at `/service-worker.js` (copied from `apps/shell/public/`
 * by the CopyRspackPlugin). Strategies:
 *   - cache-first for hashed `/assets/*.{js,css}` + fonts
 *   - stale-while-revalidate for `/api/v1/configuration/*` + `/api/v1/modules/*`
 *   - network-first navigation fallback to `/offline.html`
 *
 * Module-federation safety: the SW does NOT cache cross-origin remoteEntry.js
 * (those come from the federated UI dev servers / CDN, and stale copies would
 * break the runtime shape contract).
 */

export async function registerSW(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  // Avoid registering on the dev HMR origin — rspack's hot-update polling
  // can race against the SW's cache, producing weird stale-HMR reloads.
  // The build copies the SW to /service-worker.js so PROD picks it up
  // automatically; dev contributors who want to test the SW can flip the
  // ?sw=1 flag.
  const isDev = process.env.NODE_ENV !== 'production';
  const qsForce = new URLSearchParams(window.location.search).get('sw') === '1';
  if (isDev && !qsForce) return;

  try {
    // workbox-window is loaded lazily so the main bundle stays slim and
    // so a missing dep at build time degrades gracefully (the shell still
    // works without the SW — just without offline cache).
    const { Workbox } = await import('workbox-window');
    const wb = new Workbox('/service-worker.js', { scope: '/' });

    wb.addEventListener('waiting', () => {
      // A new SW is installed but waiting. Ask the active SW to skip
      // waiting + reload on the next page navigation so users always
      // pick up the freshest shell.
      wb.messageSkipWaiting();
    });

    wb.addEventListener('controlling', () => {
      // The new SW has taken control; the next nav will use it.
      // (We do NOT force a reload here — that would interrupt operator
      // workflows. Users get the new code on the next route change.)
    });

    await wb.register();
  } catch (err) {
    // Don't fail the shell boot if SW registration breaks; just log.
    console.warn('[registerSW] service-worker registration failed', err);
  }
}
